import { LearningMaterialSummary, useListLearningMaterialsFunctionRequest } from "shared/request/myHealthyAdvantageApi";
import { getSelectedFiltersFromUrl, selectedInterestPrefix } from "core/helpers/preferencesFilter";
import { useEffect, useState } from "react";
import { ResourceFilterMenu } from "./ResourceFilterMenu";
import { Hideable } from "shared/UI/Hideable/Hideable";
import InfiniteScroll from "react-infinite-scroll-component";
import { LearningMaterialCard } from "UIPalette/LearningMaterials/components/LearningMaterialCard";
import { PageHeader } from "UIPalette/PageHeader/PageHeader";
import PageTitle from "UIPalette/PageTitle/PageTitle";
import ViewLoading from "shared/UI/Spinners/ViewLoading";
import { ViewStateWrapper } from "UIPalette/ViewStateWrapper/ViewStateWrapper";
import { WellKnownLearningMaterialCategory, WellKnownLearningMaterialSubCategories } from "UIPalette/LearningMaterials/wellKnownLearningMaterials";
import classNames from "classnames";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { capitalizeString, getKeyByValue } from "core/helpers/utils";

export type ResourceCategoryLibraryProps = {
  category: WellKnownLearningMaterialCategory;
  hideInterests: boolean;
  pageTitle?: string;
};

type ListLearningMaterialsRequestParameters = Parameters<typeof useListLearningMaterialsFunctionRequest>[0];

const maxResults = 15;

const ResourceCategoryLibrary = ({ category, hideInterests, pageTitle }: ResourceCategoryLibraryProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [selectedInterests, setSelectedInterests] = useState<string[]>(getSelectedFiltersFromUrl(location));

  const [currentPage, setCurrentPage] = useState(1);
  const listLearningMaterialsRequestParams: ListLearningMaterialsRequestParameters = {
    page: currentPage,
    categories: [category.id],
    subCategories: selectedInterests,
    maxResults: maxResults,
  };

  const {
    data: learningMaterials = [],
    error: learningMaterialsError,
    isLoading: learningMaterialsLoading,
    mutate: refreshLearningMaterials,
  } = useListLearningMaterialsFunctionRequest(listLearningMaterialsRequestParams, { revalidateOnMountIfStale: false, showErrorIfStale: false });

  const { data: nextPageLearningMaterials = [] } = useListLearningMaterialsFunctionRequest({
    ...listLearningMaterialsRequestParams,
    page: currentPage + 1,
  });

  const [cachedLearningMaterials, setCachedLearningMaterials] = useState<LearningMaterialSummary[]>([]);
  const [hasInitialLoaded, setInitialLoaded] = useState(false);

  hideInterests = hideInterests || !!location.state?.hideInterests;
  const initialLoadComplete = () => !learningMaterialsLoading && hasInitialLoaded;
  const haveLearningMaterials = () => learningMaterials.length > 0;

  if (!initialLoadComplete() && haveLearningMaterials()) {
    setCachedLearningMaterials(cachedLearningMaterials.concat(learningMaterials));
    setInitialLoaded(true);
  }

  const resetPagination = () => {
    setInitialLoaded(false);
    setCachedLearningMaterials([]);
    setCurrentPage(1);
  };

  useEffect(() => {
    if (initialLoadComplete() && currentPage !== 1) {
      if (haveLearningMaterials()) {
        setCachedLearningMaterials(cachedLearningMaterials.concat(learningMaterials));
      }
    }

    window.location.replace(window.location.pathname + "#" + selectedInterestPrefix + selectedInterests.join("&" + selectedInterestPrefix));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, learningMaterialsLoading, selectedInterests]);

  const getHeaderTitle = () => {
    const subCategoryTitle = capitalizeString(getKeyByValue(WellKnownLearningMaterialSubCategories, selectedInterests[0]));
    const categoryTitle = t<string>(`resources.libraries.${category.path}.title`);
    const showSubcategoryTitle = selectedInterests.length === 1 && !!subCategoryTitle;
    return {
      headerTitle: showSubcategoryTitle ? subCategoryTitle : categoryTitle,
      hideTitleForMobile: selectedInterests.length !== 1 || !subCategoryTitle,
    };
  };

  const { headerTitle, hideTitleForMobile } = getHeaderTitle();

  if (learningMaterialsError) {
    return (
      <>
        <PageTitle title={`Error-${pageTitle}`} />
        <PageHeader title={headerTitle} hideForMobile={hideTitleForMobile} />
        <ViewStateWrapper
          loading={false}
          error={!!learningMaterialsError}
          errorMessage={t<string>(`resources.libraries.${category.path}.error`)}
          onRetry={refreshLearningMaterials}
          isFullScreenView={true}
        />
      </>
    );
  }

  return (
    <>
      <PageTitle title={pageTitle} />
      <PageHeader title={headerTitle} hideForMobile={hideTitleForMobile} />
      <div className={classNames("mb-6", { "col-span-full": hideInterests, "flex justify-between items-center": !hideInterests })}>
        <p role="doc-subtitle">{t<string>(`resources.libraries.${category.path}.description`)}</p>
        <Hideable hidden={hideInterests}>
          <ResourceFilterMenu
            preSelectedFilters={selectedInterests}
            onApply={(filters) => {
              setSelectedInterests(filters);
              resetPagination();
            }}
          />
        </Hideable>
      </div>
      <div className="col-span-full">
        <InfiniteScroll
          dataLength={cachedLearningMaterials.length}
          next={() => setCurrentPage(currentPage + 1)}
          hasMore={nextPageLearningMaterials.length > 0}
          loader={learningMaterialsLoading && <ViewLoading containerClass="col-span-full h-[100px]" />}
          endMessage={<></>}
          className="grid grid-cols-1 lg:grid-cols-2 lg:gap-x-[24px]"
        >
          {cachedLearningMaterials?.map((item) => {
            return <LearningMaterialCard key={item.id} item={item} navigateBasePath={`/${category.path}/`} />;
          })}
        </InfiniteScroll>
      </div>
    </>
  );
};

ResourceCategoryLibrary.defaultProps = { hideInterests: false };
export default ResourceCategoryLibrary;
